import { Paper, Tab, Tabs, Typography, styled } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { keyframes, styled as muiStyled } from '@mui/material/styles';
import { StyledDataGrid as UserRequestsDataGrid } from '../../../userRequests/UserRequestsStyles';

export const GRID_REGULAR_TYPOGRAPHY = {
  fontFamily: 'Open Sans',
  fontSize: 14,
  fontWeight: 400,
};

export const GRID_BOLD_TYPOGRAPHY = {
  ...GRID_REGULAR_TYPOGRAPHY,
  fontWeight: 700,
};

export const RoleContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  overflow: 'hidden',
  width: '100%',
});

export const RoleName = styled(Typography)({
  ...GRID_BOLD_TYPOGRAPHY,
});

export const RoleDescription = styled(Typography)(({ theme }) => ({
  ...GRID_REGULAR_TYPOGRAPHY,
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const StyledDeleteIcon = styled(DeleteIcon)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '1.25rem',
  marginLeft: 4,
}));

export const DataGridContainer = styled(Paper)({
  height: '100%',
  overflow: 'hidden',
  width: '100%',
});

export const StyledDataGrid = styled(UserRequestsDataGrid)({
  border: 0,
  '& .MuiDataGrid-toolbar': {
    minHeight: 0,
    height: 'auto',
    padding: 0,
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  '& .MuiDataGrid-cell': {
    ...GRID_REGULAR_TYPOGRAPHY,
    alignItems: 'center',
    borderBottom: 'none !important',
    borderTop: 'none !important',
    display: 'flex',
    padding: '0 16px',
    outline: 'none !important',
    '&:focus': {
      outline: 'none !important',
      boxShadow: 'none',
    },
    '&:focus-within': {
      outline: 'none !important',
      boxShadow: 'none',
    },
  },
  '& .MuiDataGrid-columnHeaders': {
    borderBottom: 'none',
  },
  '& .MuiDataGrid-columnHeader': {
    alignItems: 'center',
    padding: '0 16px',
  },
  '& .MuiDataGrid-row': {
    borderBottom: 'none !important',
    borderTop: 'none !important',
  },
  '& .MuiDataGrid-row--borderBottom .MuiDataGrid-cell': {
    borderBottom: 'none !important',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    ...GRID_BOLD_TYPOGRAPHY,
  },
  '& .MuiChip-label': {
    ...GRID_REGULAR_TYPOGRAPHY,
  },
  '& .MuiDataGrid-cell.actions-cell': {
    gap: 4,
  },
});

export const ToolbarContainer = styled(Box)({
  alignItems: 'center',
  boxSizing: 'border-box',
  display: 'flex',
  flexWrap: 'nowrap',
  height: 52,
  maxHeight: 52,
  minHeight: 52,
  overflow: 'hidden',
  padding: '0 16px',
  width: '100%',
});

export const ToolbarStart = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  gap: 16,
  minWidth: 0,
});

export const ToolbarActions = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  gap: 16,
  marginLeft: 'auto',
});

export const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderRadius: 20,
  fontSize: 14,
  fontWeight: 700,
  height: 36,
  maxHeight: 36,
  minHeight: '36px !important',
  '& .MuiTabs-scroller': {
    height: 36,
  },
  '& .MuiTabs-flexContainer': {
    height: 36,
  },
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.common.black,
    borderRadius: 18,
    height: '100%',
    zIndex: 0,
  },
}));

export const StyledTab = styled(Tab)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontFamily: 'Red Hat Display',
  fontSize: 14,
  fontWeight: 700,
  height: 36,
  maxHeight: 36,
  minHeight: '36px !important',
  minWidth: 0,
  opacity: 1,
  padding: '0 16px',
  textTransform: 'none',
  transition: theme.transitions.create('color', {
    duration: theme.transitions.duration.short,
  }),
  zIndex: 1,
  '&:hover': {
    color: `${theme.palette.common.black} !important`,
  },
  '&.Mui-selected': {
    color: theme.palette.common.white,
  },
  '&.Mui-selected:hover': {
    color: `${theme.palette.common.white} !important`,
  },
}));

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const GridTransition = muiStyled('div')({
  animation: `${fadeIn} 180ms ease-out`,
  height: '100%',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
});

export const SearchField = muiStyled(TextField)({
  '& > div': {
    borderRadius: 32,
    minWidth: 320,
  },
  '& input': {
    ...GRID_REGULAR_TYPOGRAPHY,
  },
  '@media (max-width: 600px)': {
    '& > div': {
      minWidth: 200,
    },
  },
});

export const ToolbarIconButton = muiStyled(IconButton)({
  padding: 0,
});

export const GridActionButton = muiStyled(IconButton)({
  padding: 4,
});
